import type { GraphEdge, GraphNode, OwnershipGraphData } from '../../../types'
import type { PositionedNode } from './layoutTree'
import {
  canExpandDown,
  canExpandDownRoot,
  canExpandUp,
} from './graphExpandEligibility'
import { countryDisplayName } from '../../../utils/format'
import {
  COMPANY_NODE_RX,
  GRAPH_COLORS as C,
  GRAPH_FONT,
  NODE_FLAG_H,
  NODE_FLAG_W,
  NODE_LABEL_FONT_SIZE,
  graphNodeStyle,
  nodeHasForeignCountry,
} from './graphPalette'

const MAX_COMPANY_LABEL_CHARS = 21
const MAX_PERSON_LABEL_CHARS = 20

export interface GraphSvgEdgeRow {
  d: string
  edge: GraphEdge
}

export interface BuildOwnershipGraphSvgInput {
  graphData: OwnershipGraphData
  positions: Record<string, PositionedNode>
  edgeRows: GraphSvgEdgeRow[]
  beneficialNodeIds: Set<string>
  svgWidth: number
  svgHeight: number
  /** Raw flag SVG per upper-case country code, embedded under foreign nodes. */
  flagMarkup?: Record<string, string>
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function truncateLabel(navn: string, maxChars: number): string {
  if (navn.length <= maxChars) return navn
  return navn.slice(0, maxChars - 1) + '…'
}

/** Place a standalone flag SVG at (x, y) as a nested, sized <svg>. */
function nestedFlagMarkup(markup: string, x: number, y: number): string | null {
  const open = markup.indexOf('<svg')
  if (open < 0) return null
  const body = markup.slice(open + 4)
  return `<svg x="${x}" y="${y}" width="${NODE_FLAG_W}" height="${NODE_FLAG_H}" preserveAspectRatio="none"${body}`
}

function expandChevronMarkup(cx: number, y: number, direction: 'up' | 'down'): string {
  const path = direction === 'down' ? 'M-5 -2 L0 4 L5 -2' : 'M-5 4 L0 -2 L5 4'
  return `<g transform="translate(${cx}, ${y})">
  <circle r="14" fill="${C.surface}" stroke="${C.nodeStroke}" stroke-width="1"/>
  <path d="${path}" fill="none" stroke="${C.inkMuted}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
</g>`
}

function nodeMarkup(
  node: GraphNode,
  pos: PositionedNode,
  isBeneficialOwner: boolean,
  flagMarkup: Record<string, string> | undefined,
): string {
  const { x, y, w, h } = pos
  const isPerson = node.kind === 'person'
  const isTrunc = !!node.truncated
  const rx = isPerson ? h / 2 : COMPANY_NODE_RX
  const style = graphNodeStyle(node, { isBeneficialOwner })

  const maxChars = isPerson ? MAX_PERSON_LABEL_CHARS : MAX_COMPANY_LABEL_CHARS
  const displayName = escapeXml(truncateLabel(node.navn || '—', maxChars))
  const hasCountry = nodeHasForeignCountry(node)
  const textY = hasCountry ? y + h / 2 - 4 : y + h / 2 + 4.5

  let out = `<g data-node-id="${escapeXml(node.id)}">
  <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}"/>
  <text x="${x + w / 2}" y="${textY}" text-anchor="middle" font-size="${NODE_LABEL_FONT_SIZE}" font-family="${GRAPH_FONT}" font-weight="${style.fontWeight}" fill="${style.text}">${displayName}</text>`

  if (hasCountry) {
    const code = node.countryCode!.toUpperCase()
    const name = escapeXml(countryDisplayName(code))
    const fx = x + w / 2 - NODE_FLAG_W / 2
    const fy = y + h / 2 + 2
    const flag = flagMarkup?.[code] ? nestedFlagMarkup(flagMarkup[code], fx, fy) : null
    if (flag) {
      out += `\n  <g><title>${name}</title>${flag}<rect x="${fx + 0.5}" y="${fy + 0.5}" width="${NODE_FLAG_W - 1}" height="${NODE_FLAG_H - 1}" rx="1.5" fill="none" stroke="${C.ink}" stroke-opacity="0.22"/></g>`
    } else {
      out += `\n  <text x="${x + w / 2}" y="${y + h / 2 + 13}" text-anchor="middle" font-size="10.5" font-family="${GRAPH_FONT}" fill="${style.text}">${name}</text>`
    }
  }

  if (isTrunc) {
    out += `\n  <text x="${x + w - 8}" y="${y + h / 2 + 4}" text-anchor="middle" font-size="11" fill="${C.inkFaint}" font-family="${GRAPH_FONT}">…</text>`
  }

  out += '\n</g>'
  return out
}

function collectExpandMarkers(
  graphData: OwnershipGraphData,
  positions: Record<string, PositionedNode>,
): string[] {
  const rootId = 'cvr:' + graphData.rootCvr
  const markers: string[] = []

  if (canExpandDownRoot(graphData)) {
    const rootPos = positions[rootId]
    if (rootPos) {
      markers.push(expandChevronMarkup(
        rootPos.x + rootPos.w / 2,
        rootPos.y + rootPos.h + 22,
        'down',
      ))
    }
  }

  for (const node of graphData.nodes) {
    const pos = positions[node.id]
    if (!pos) continue

    if (canExpandUp(node, rootId, graphData.edges)) {
      markers.push(expandChevronMarkup(pos.x + pos.w / 2, pos.y - 22, 'up'))
    }
    if (canExpandDown(node, rootId, graphData.edges)) {
      markers.push(expandChevronMarkup(pos.x + pos.w / 2, pos.y + pos.h + 22, 'down'))
    }
  }

  return markers
}

/** Build a standalone SVG string for the current graph state (full canvas). */
export function buildOwnershipGraphSvg(input: BuildOwnershipGraphSvgInput): string {
  const { graphData, positions, edgeRows, beneficialNodeIds, svgWidth, svgHeight, flagMarkup } = input

  const edgeParts: string[] = []
  for (const row of edgeRows) {
    if (row.edge.kind === 'beneficial') continue
    edgeParts.push(
      `<path d="${row.d}" fill="none" stroke="${C.edge}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
    )
  }

  const nodeParts: string[] = []
  for (const node of graphData.nodes) {
    const pos = positions[node.id]
    if (!pos) continue
    nodeParts.push(nodeMarkup(node, pos, beneficialNodeIds.has(node.id), flagMarkup))
  }

  const expandParts = collectExpandMarkers(graphData, positions)

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgWidth} ${svgHeight}" width="${svgWidth}" height="${svgHeight}" role="img" aria-label="Ejerskabsgraf">
  <rect width="100%" height="100%" fill="${C.graphCanvas}"/>
  <g class="ownership-graph-edges">${edgeParts.join('\n  ')}</g>
  <g class="ownership-graph-nodes">${nodeParts.join('\n  ')}</g>
  <g class="ownership-graph-expand-markers">${expandParts.join('\n  ')}</g>
</svg>`
}

export function ownershipGraphSvgFilename(rootCvr: string): string {
  return `ejerskabsgraf-${rootCvr}.svg`
}

export function ownershipGraphPngFilename(rootCvr: string): string {
  return `ejerskabsgraf-${rootCvr}.png`
}

/** Save a blob as a file via a temporary link. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function downloadOwnershipGraphSvg(svg: string, filename: string): void {
  downloadBlob(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }), filename)
}

/**
 * Rasterise a standalone SVG string to PNG at `scale` × its size (sharp in
 * articles and slides). The SVG must not reference external resources, or
 * the canvas is tainted; flags are embedded as markup for that reason.
 */
export function svgToPngBlob(
  svg: string,
  width: number,
  height: number,
  scale = 2,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))
    const img = new Image()
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = Math.ceil(width * scale)
        canvas.height = Math.ceil(height * scale)
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas er ikke tilgængelig.')
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0, width, height)
        canvas.toBlob((blob) => {
          if (blob) resolve(blob)
          else reject(new Error('Kunne ikke lave PNG.'))
        }, 'image/png')
      } catch (err) {
        reject(err)
      } finally {
        URL.revokeObjectURL(url)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Kunne ikke læse grafen som billede.'))
    }
    img.src = url
  })
}

/** Fetch raw flag SVGs for every foreign node, keyed by upper-case code. */
export async function loadGraphFlagMarkup(
  nodes: GraphNode[],
  fetchFlag: (code: string) => Promise<string | null>,
): Promise<Record<string, string>> {
  const codes = new Set<string>()
  for (const node of nodes) {
    if (nodeHasForeignCountry(node)) codes.add(node.countryCode!.toUpperCase())
  }
  const entries = await Promise.all(
    [...codes].map(async (code) => [code, await fetchFlag(code)] as const),
  )
  const out: Record<string, string> = {}
  for (const [code, markup] of entries) {
    if (markup) out[code] = markup
  }
  return out
}
